import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { DateTime } from "luxon";
import React from "react";
import { Link, Outlet, useSearchParams } from "react-router-dom";

import { useHasPermission } from "@/common/acl/guard/guard";
import { NoData } from "@/common/components/info/info";
import { Metadata } from "@/common/components/metadata/metadata";
import {
  itemsPerPage,
  Pagination,
} from "@/common/components/pagination/pagination";
import { SearchParamsLink } from "@/common/components/search-params-link/search-params-link";
import { Spacer } from "@/common/components/spacer/spacer";
import { Spinner } from "@/common/components/spinner/spinner";
import { UserIdentity } from "@/common/components/user-identity/user-identity";
import { GetUsers1ApprovalState } from "@/generated/api/users";
import { AutocompleteString } from "@/types";

import { Filter } from "../components/table/filter/filter";
import { Table } from "../components/table/table";
import { UserStateAction } from "../components/user-actions/user-state-action";
import { pendingUsersQuery } from "./pending-users.api";

type TableMeta = {
  filterVariant?: "text" | "select";
  filterKey?: "search";
  label: () => string;
};

type User = {
  createdAt: DateTime;
  id: number;
  email: string;
  company: {
    name: string;
    country: string;
    number: string;
    id: string;
  };
  approvalStatus: GetUsers1ApprovalState;
  userId: string;
};

const columnHelper = createColumnHelper<User>();

const path = "pending-users";

// @ts-expect-error I don't care about the type here
const fallback = [];

const Users = () => {
  const { params, handleParamChange } = usePendingUsersParams();
  const pendingUsers = useQuery({
    placeholderData: keepPreviousData,
    ...pendingUsersQuery({
      page: params.page,
      search: params.search,
    }),
  });
  const hasCompanyApprovePermission = useHasPermission("users:company:approve");
  const columns = React.useMemo(
    () => [
      columnHelper.accessor("id", {
        header: () => "ID",
      }),
      columnHelper.accessor("userId", {}),
      columnHelper.accessor("email", {
        cell: (info) => {
          return (
            <SearchParamsLink
              className="flex gap-2 text-can-forest-teal"
              to={info.row.getValue<User["id"]>("id").toString()}
            >
              <UserIdentity identity={info.row.getValue("userId")} />
              {info.getValue()}
            </SearchParamsLink>
          );
        },
        header: () => t`E-mail`,
        meta: {
          filterKey: "search",
          filterVariant: "text",
          label: () => t`E-mail`,
        } as TableMeta,
      }),

      columnHelper.accessor("createdAt", {
        cell: (info) => info.getValue().toLocaleString(DateTime.DATE_SHORT),
        enableColumnFilter: false,
        header: () => t`Datum registrace`,
        meta: {
          label: () => t`Datum registrace`,
        } as TableMeta,
      }),

      columnHelper.accessor("company", {
        cell: (info) => (
          <Link
            className="text-can-forest-teal"
            to={"company/" + info.getValue().id}
          >
            {info.getValue().country +
              " " +
              info.getValue().name +
              " / " +
              info.getValue().number}
          </Link>
        ),
        header: () => t`Společnost`,
        meta: {
          filterKey: "search",
          filterVariant: "text",
          label: () => t`Společnost`,
        } as TableMeta,
      }),
      ...(hasCompanyApprovePermission
        ? [
            columnHelper.display({
              cell: ({ row }) => (
                <UserStateAction
                  initialStatus="WAITING"
                  userCompanyRelationId={row.getValue("id")}
                />
              ),
              id: "actions",
            }),
          ]
        : []),
    ],
    [hasCompanyApprovePermission],
  );

  const table = useReactTable({
    columns,
    // @ts-expect-error I don't care about the type here
    data: pendingUsers.data?.users ?? fallback,
    filterFns: {},
    getCoreRowModel: getCoreRowModel(),
    manualFiltering: true,
    manualPagination: true,
    state: {
      columnVisibility: {
        id: false,
        userId: false,
      },
    },
  });

  if (pendingUsers.status === "pending" && pendingUsers.isLoading) {
    return (
      <>
        <Metadata title={t`Čekající na schválení`} />
        <Spinner className="mt-4" />
      </>
    );
  }

  return (
    <>
      <Metadata title={t`Čekající na schválení`} />
      <label className="mx-4 flex items-baseline gap-x-4">
        <Trans>Hledat e-mail, jméno/IČO společnosti</Trans>
        <Filter
          column={table.getColumn("email")!}
          onFilterChange={handleParamChange}
          value={params.search}
        />
      </label>
      <Spacer className="h-4" />
      <Table>
        <Table.Head table={table} />
        {pendingUsers.status === "success" &&
        pendingUsers.data.users.length > 0 ? (
          <Table.Body table={table} />
        ) : null}
      </Table>
      {pendingUsers.data ? (
        <>
          <Spacer className="h-5" />
          <Pagination
            getter={() => parseInt(params.page, 10)}
            pageSize={itemsPerPage.oneHundred}
            setter={(page) => handleParamChange("page", page.toString())}
            total={pendingUsers.data.totalSize}
          />
        </>
      ) : null}
      {pendingUsers.status === "success" &&
      pendingUsers.data.users.length === 0 ? (
        <div className="mt-4">
          <NoData>
            <Trans>Nebyly nalezeny žádné záznamy</Trans>
          </NoData>
        </div>
      ) : null}
      {pendingUsers.fetchStatus === "fetching" && !pendingUsers.isLoading ? (
        <Spinner withHint={false} />
      ) : null}
      <Outlet />
    </>
  );
};

const usePendingUsersParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const params = {
    page: searchParams.get("page") ?? "1",
    search: searchParams.get("search") ?? "",
  };

  const handleParamChange = (
    key: AutocompleteString<"search">,
    value: string,
  ) => {
    setSearchParams((c) => {
      c.set(key, value);
      return c;
    });
  };

  return { handleParamChange, params };
};

export { path, Users };
